import {
  SalvButton,
  SalvDropdown,
  SalvFieldHeader,
  SalvNumberField,
  SalvRadioToggleButton,
  SalvRadioToggleGroup,
  SalvTextField,
  Size,
} from '@salvapp/ui-components';
import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare } from '@fortawesome/pro-solid-svg-icons/faPenToSquare';
import { FieldArray, FieldArrayRenderProps, useFormikContext } from 'formik';
import { ScenarioVersionModalFormValues } from './ScenarioVersionModal';
import { camelCaseToSentence, convertToDropdownOption, fromSnakeCase, toSnakeCase } from '../../../common/helper';
import './ParametersEditor.scss';
import { faDollar, faXmarkLarge } from '@fortawesome/pro-solid-svg-icons';
import { DropdownOption } from '@salvapp/ui-components/build/dropdown/dropdown.types';
import {
  InternalApiScenarioIntervalTimeUnit,
  InternalApiScenarioVersionParameter,
  InternalApiScenarioVersionParameterTimeUnit,
  InternalApiScenarioVersionParameterType,
} from '../../../api-internal';
import { RESERVED_SCENARIO_PARAMETER_NAMES, SCENARIO_PARAMETER_REGEX } from '../../../common/versioning/helper';

const parameterTypeNames = {
  [InternalApiScenarioVersionParameterType.NUMERIC]: 'Numeric',
  [InternalApiScenarioVersionParameterType.PERIOD]: 'Period',
  [InternalApiScenarioVersionParameterType.ARRAY]: 'Array',
};

const maxPeriodValues: { [key in InternalApiScenarioVersionParameterTimeUnit]: number } = {
  [InternalApiScenarioVersionParameterTimeUnit.HOUR]: 8760,
  [InternalApiScenarioVersionParameterTimeUnit.DAY]: 365,
  [InternalApiScenarioVersionParameterTimeUnit.WEEK]: 52,
  [InternalApiScenarioVersionParameterTimeUnit.MONTH]: 12,
};

export const ParameterValuesView = ({ disabled }: { disabled: boolean }) => {
  const { values, errors } = useFormikContext<ScenarioVersionModalFormValues>();

  if (values.parameters.length === 0) {
    return <div className='text-secondary text-xs'>No parameters defined</div>;
  }

  return (
    <FieldArray name='parameters'>
      {(arrayHelpers) => {
        return (
          <div className='parameters-view'>
            {values.parameters.map((parameter, index) => {
              return (
                <React.Fragment key={index}>
                  <div className='text-small m-r-8'>{parameter.displayName}</div>

                  <div className='d-flex'>
                    {parameter.type === InternalApiScenarioVersionParameterType.NUMERIC && (
                      <SalvNumberField
                        size={Size.SMALL}
                        onChange={(value) => {
                          arrayHelpers.replace(index, {
                            ...parameter,
                            value,
                          });
                        }}
                        className='parameter-number-field'
                        value={parameter.value as unknown as number}
                        isInvalid={!!errors.parameters?.[index]}
                        ariaLabel={`value-${index}`}
                        disabled={disabled}
                      />
                    )}
                    {parameter.type === InternalApiScenarioVersionParameterType.PERIOD && (
                      <>
                        <SalvNumberField
                          size={Size.SMALL}
                          onChange={(value) => {
                            arrayHelpers.replace(index, {
                              ...parameter,
                              value: parameter.timeUnit && value ? Math.floor(value) : value,
                            });
                          }}
                          className='parameter-number-field'
                          value={parameter.value as unknown as number}
                          isInvalid={!!errors.parameters?.[index]}
                          ariaLabel={`value-${index}`}
                          disabled={disabled}
                          maxValue={parameter.timeUnit ? maxPeriodValues[parameter.timeUnit] : undefined}
                        />
                        <SalvRadioToggleGroup
                          value={parameter.timeUnit}
                          onChange={(timeUnit) => {
                            arrayHelpers.replace(index, {
                              ...parameter,
                              timeUnit,
                              value:
                                (parameter.value as unknown as number) >
                                maxPeriodValues[timeUnit as InternalApiScenarioVersionParameterTimeUnit]
                                  ? maxPeriodValues[timeUnit as InternalApiScenarioVersionParameterTimeUnit]
                                  : parameter.value,
                            });
                          }}
                          size={Size.SMALL}
                          disabled={disabled}
                          required={true}
                          ariaLabel={`timeunit-${index}`}
                        >
                          <SalvRadioToggleButton value={InternalApiScenarioIntervalTimeUnit.HOUR} label='Hour' />
                          <SalvRadioToggleButton value={InternalApiScenarioIntervalTimeUnit.DAY} label='Day' />
                          <SalvRadioToggleButton value={InternalApiScenarioIntervalTimeUnit.WEEK} label='Week' />
                          <SalvRadioToggleButton value={InternalApiScenarioIntervalTimeUnit.MONTH} label='Month' />
                        </SalvRadioToggleGroup>
                      </>
                    )}
                    {parameter.type === InternalApiScenarioVersionParameterType.ARRAY && (
                      <SalvDropdown
                        size={Size.SMALL}
                        onChange={(value) => {
                          arrayHelpers.replace(index, {
                            ...parameter,
                            value: value.map((option) => option.label),
                          });
                        }}
                        isInvalid={!!errors.parameters?.[index]}
                        ariaLabel={`value-${index}`}
                        disabled={disabled}
                        options={
                          (parameter.value as string[])?.map((option) => convertToDropdownOption(option, option)) || []
                        }
                        value={
                          (parameter.value as string[])?.map((option) => convertToDropdownOption(option, option)) || []
                        }
                        isExtendable={true}
                        isMulti={true}
                      />
                    )}
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        );
      }}
    </FieldArray>
  );
};

const ScenarioParameterEditorRow = ({
  parameter,
  index,
  replace,
  remove,
}: {
  parameter: InternalApiScenarioVersionParameter;
  index: number;
  replace: FieldArrayRenderProps['replace'];
  remove: FieldArrayRenderProps['remove'];
}) => {
  const [isNameValid, setNameValid] = useState(true);
  const [isFirstTyped, setFirstTyped] = useState(!parameter.name);
  const removeSpaces = (value: string) => value.replaceAll(/\s/g, '');
  const { values } = useFormikContext<ScenarioVersionModalFormValues>();

  const getParameterTypeOptions = (): DropdownOption[] => {
    return Object.entries(parameterTypeNames).map((type) => ({
      label: type[1],
      key: type[0],
      value: type[0],
    }));
  };

  const firstInputRef = useRef<HTMLInputElement>(null);
  // focus the first parameter input
  useEffect(() => {
    if (index === 0 && firstInputRef.current) {
      firstInputRef.current.focus();
    }
  }, [firstInputRef]);

  return (
    <div className='parameter-row'>
      <div>
        {index === 0 && <div className='parameter-editor-header'>Display Name</div>}
        <SalvTextField
          ref={firstInputRef}
          value={parameter.displayName}
          onChange={(value) => {
            const newName = toSnakeCase(value);
            setNameValid(
              isParameterNameValid(
                newName,
                values.parameters.map((p) => p.name)
              )
            );

            replace(index, {
              ...parameter,
              displayName: value,
              name: isFirstTyped ? newName : parameter.name,
            });
          }}
          onBlur={() => setFirstTyped(false)}
          size={Size.SMALL}
          ariaLabel={`display-name-${index}`}
        />
      </div>

      <div>
        {index === 0 && <div className='parameter-editor-header'>Parameter Name</div>}
        <SalvTextField
          icon={faDollar}
          value={parameter.name}
          className='parameter-name-field'
          onChange={(value) => {
            const newName = removeSpaces(value);
            setNameValid(
              isParameterNameValid(
                newName,
                values.parameters.map((p) => p.name)
              )
            );

            replace(index, {
              ...parameter,
              name: newName,
              displayName: isFirstTyped ? fromSnakeCase(camelCaseToSentence(newName)) : parameter.displayName,
            });
          }}
          onBlur={() => setFirstTyped(false)}
          size={Size.SMALL}
          isInvalid={!isNameValid}
          ariaLabel={`name-${index}`}
        />
      </div>

      <div>
        {index === 0 && <div className='parameter-editor-header'>Type</div>}
        <SalvDropdown
          options={getParameterTypeOptions()}
          value={convertToDropdownOption(parameterTypeNames[parameter.type], parameter.type)}
          onChange={(value) => {
            const type = value[0].value;
            replace(index, {
              ...parameter,
              type: type,
              timeUnit:
                type === InternalApiScenarioVersionParameterType.PERIOD
                  ? InternalApiScenarioVersionParameterTimeUnit.WEEK
                  : undefined,
              value: type === InternalApiScenarioVersionParameterType.ARRAY ? [] : 0,
            });
          }}
          size={Size.SMALL}
          clearable={false}
          ariaLabel='type'
        />
      </div>

      <SalvButton icon={faXmarkLarge} variant='muted' size={Size.SMALL} onClick={() => remove(index)} />
    </div>
  );
};

const getNewParameter = (): InternalApiScenarioVersionParameter => ({
  name: '',
  displayName: '',
  type: InternalApiScenarioVersionParameterType.NUMERIC,
  value: 0 as unknown as object,
});

const ParametersEditorView = ({ close }: { close: () => void }) => {
  const { values } = useFormikContext<ScenarioVersionModalFormValues>();

  return (
    <div className='parameters-editor'>
      <FieldArray name='parameters'>
        {(arrayHelpers) => {
          return (
            <div>
              {values.parameters.map((parameter, index) => {
                return (
                  <ScenarioParameterEditorRow
                    key={index}
                    parameter={parameter}
                    replace={arrayHelpers.replace}
                    remove={arrayHelpers.remove}
                    index={index}
                  />
                );
              })}

              <div className='d-flex'>
                <SalvButton
                  label='Add parameter'
                  variant='secondary'
                  size={Size.SMALL}
                  onClick={() => {
                    arrayHelpers.push(getNewParameter());
                  }}
                  className='m-r-8'
                />

                <SalvButton
                  label='Confirm'
                  variant='primary'
                  size={Size.SMALL}
                  onClick={close}
                  disabled={values.parameters.some((p) => {
                    const isValid =
                      p.displayName &&
                      isParameterNameValid(
                        p.name,
                        values.parameters.map((p) => p.name)
                      );
                    return !isValid;
                  })}
                />
              </div>
            </div>
          );
        }}
      </FieldArray>
    </div>
  );
};

const ParametersEditor = ({ disabled }: { disabled: boolean }) => {
  const { values, setFieldValue } = useFormikContext<ScenarioVersionModalFormValues>();

  const [isEditing, setEditing] = useState(false);

  return (
    <div>
      <SalvFieldHeader
        className='m-b-4'
        size={Size.MEDIUM}
        label={
          <div>
            <span>Parameters</span>
            {!isEditing && !disabled && (
              <FontAwesomeIcon
                icon={faPenToSquare}
                onClick={async () => {
                  if (values.parameters.length === 0) {
                    await setFieldValue('parameters', [getNewParameter()]);
                  }
                  setEditing(true);
                }}
                className='text-secondary cursor-pointer m-l-8'
                aria-label='edit parameters'
              />
            )}
          </div>
        }
      />
      {isEditing ? (
        <ParametersEditorView close={() => setEditing(false)} />
      ) : (
        <ParameterValuesView disabled={disabled} />
      )}
    </div>
  );
};

export const isParameterNameValid = (name: string, allNames: string[]) => {
  if (!name) return false;
  if (RESERVED_SCENARIO_PARAMETER_NAMES.includes(name)) return false;
  if (allNames.filter((existing) => existing === name).length > 1) {
    return false;
  }
  return SCENARIO_PARAMETER_REGEX.test('$' + name);
};

export const getValidParameters = (parameters: InternalApiScenarioVersionParameter[]) => {
  return parameters.filter((p) =>
    isParameterNameValid(
      p.name,
      parameters.map((p) => p.name)
    )
  );
};

export default ParametersEditor;
